// Dominio del modo "Replicar una foto" — el prompt y las reglas que traducen
// "esta foto (+ este producto)" a un pedido de generación.
// Ver ADR-2026-09-10-modo-replicar-foto-belleza y
// ADR-2026-09-13-replicar-una-foto-arranca-en-configurar-y-el-producto-es-opcional.
//
// Vive aparte de la pantalla (`modes/ReplicarFotoMode.tsx`) por lo mismo que
// `mostrarComoFuncionaBelleza.ts`: acá está lo que hay que tocar cuando los
// resultados no salen bien, y no debería obligar a abrir un componente de 600
// líneas para cambiar una frase.
//
// LAS REFERENCIAS VIAJAN ETIQUETADAS. `reference_images` lleva cada imagen con su
// `tipo`, y el backend le antepone la etiqueta canónica en el pedido al motor
// (ADR-2026-09-08-las-referencias-del-agente-viajan-etiquetadas): la foto a
// replicar va como `adjunto` → "Imagen de referencia:", el producto como `producto`
// con su `productId` → "Producto 1 vista frontal:" (el backend baja sus vistas), y la
// modelo como `modelo` → "Modelo de referencia:". Este prompt las nombra POR ESA
// ETIQUETA, así que el orden del array da igual. Antes decía "la primera / la
// segunda" y mandaba URLs sueltas; con el contrato etiquetado eso era frágil y
// además no compilaba (ADR-2026-09-13-replicar-una-foto-orden-modelo-y-referencias-etiquetadas).
// Los textos de las etiquetas son los del backend (`etiquetaDe` en
// `services/ads/referencias.ts`): si cambian allá, cambian acá.
//
// El producto y la modelo son OPCIONALES. Sin producto (`conProducto: false`) el
// producto pasa a ser el que ya esté en la foto, si hay, y lo que se replica es la
// escena con los cambios que pida el usuario ("otro fondo", "sin la modelo"). Con
// modelo, esa persona reemplaza a la de la foto (o se suma si no hay ninguna).
//
// TRES PIPELINES, uno por nivel de fidelidad (ADR-2026-09-13-replicar-una-foto-tres-pipelines):
//   • `describir` (Inspirada en la foto): la foto pasa por VISIÓN (`/v1/media/analyze`,
//     gratis) con `promptParaDescribir`, que devuelve el prompt que la recrea con los
//     cambios; se genera con `creativo_plus` a partir de ESE prompt y SIN la foto como
//     referencia (`promptNivelLibre` le agrega las reglas del producto/modelo), por la
//     cadena de CREACIÓN normal —Gemini/Nano Banana primero— (ADR-2026-10-08-inspirada-va-con-nano-banana).
//   • `referencia` (Equilibrada): `creativo_plus` con la foto como referencia etiquetada
//     y `promptReplicar` — lo que el modo hizo siempre.
//   • `editar` (Igual a la foto): `/v1/images/edit` modo `replicar` — el backend EDITA la
//     foto en un solo pedido (producto, modelo, instrucción y formato); el prompt vive
//     allá (`replicarPrompt` en atlas-backend).
// El mapa está en `comoSeGenera`; la pantalla bifurca por él y no por el id.
//
// EN PANTALLA HAY DOS OPCIONES, no tres (ADR-2026-10-08-dos-opciones-de-fidelidad):
// "Inspirada en la foto" (`describir`) e "Igual a la foto". La segunda elige el camino
// según haya o no una PERSONA nueva: sin persona, `referencia` con la instrucción de
// Equilibrada (Sunburst primero); con persona, `editar` (Seedream primero, lo decide el
// backend). "Equilibrada" queda como nivel de PROMPT, no como opción.

import { RUBRO_MODA } from '../../lib/rubro';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

/**
 * El RUBRO decide qué se dice del producto y de cómo lo usa la modelo
 * (ADR-2026-09-14-replicar-una-foto-habla-del-rubro). Dos familias y no una por rubro:
 * lo que cambia de verdad es si el producto se LLEVA PUESTO (moda: prenda, calzado,
 * accesorio — talle, caída, outfit) o se USA/APOYA/SOSTIENE (todo lo demás: envase,
 * objeto, mueble, comida — contacto, sombra de apoyo, escala en la mano). Antes el
 * prompt hablaba de "envase, etiqueta y packaging" para todos, que es lo que un vestido
 * no tiene.
 */
export type RubroPrompt = 'moda' | 'generico';

export const rubroDelPrompt = (rubro?: string | null): RubroPrompt =>
  rubro === RUBRO_MODA ? 'moda' : 'generico';

/** Cómo nombra el backend a cada referencia en el pedido al motor. Sólo para que el
 *  prompt las llame igual. */
export const ETIQUETAS = {
  foto: 'Imagen de referencia',
  producto: 'Producto',
  modelo: 'Modelo de referencia',
} as const;

/**
 * Quién es cada referencia. Es lo PRIMERO que se dice, porque todo lo demás cuelga de
 * acá. Con VARIOS productos el backend los numera ("Producto 1 vista K:", "Producto 2
 * vista K:") y hay que decir que son distintos: sin eso el motor los promedia
 * (ADR-2026-09-14-replicar-una-foto-con-varios-productos).
 */
const promptIntro = (cantidadProductos: number): string =>
  `Las imágenes vienen etiquetadas. La etiquetada "${ETIQUETAS.foto}" es la foto de ` +
  'la que hay que partir. ' +
  (cantidadProductos > 1
    ? `Las etiquetadas "${ETIQUETAS.producto} 1", "${ETIQUETAS.producto} 2"… (${cantidadProductos} ` +
      'productos) son los productos REALES de la marca, cada uno en sus distintas vistas: ' +
      'son productos DISTINTOS, no mezcles los rasgos de uno con los de otro.'
    : `Las etiquetadas "${ETIQUETAS.producto}" son el producto REAL de la marca, en sus ` +
      'distintas vistas.');

/** Lo que se suma a las reglas del producto cuando van VARIOS. */
const PROMPT_VARIOS_PRODUCTOS =
  'Van TODOS los productos, cada uno una sola vez: el que reemplaza a uno de la foto va ' +
  'en su lugar, y los que no tienen a quién reemplazar se ubican donde se combinen ' +
  'naturalmente con la escena y entre sí, sin taparse. No inventes productos que no ' +
  'estén en las referencias.';

/** La intro cuando NO hay producto: hay que decir que no viene una foto de producto
 *  aparte, para que el modelo no tome otra referencia como si lo fuera. */
const PROMPT_INTRO_SIN_PRODUCTO =
  `La imagen etiquetada "${ETIQUETAS.foto}" es la foto de la que hay que partir. No ` +
  'viene una foto de producto aparte.';

/**
 * Las reglas del PRODUCTO, iguales en los tres niveles de fidelidad: lo que
 * cambia con la fidelidad es cuánto se copia de la escena, nunca cuánto se respeta
 * el producto.
 *
 * Las dos reglas duras, por orden de cuánto duele que fallen:
 *   1. El producto es el REAL: envase, color, etiqueta y proporciones exactas.
 *      Un labial con la etiqueta inventada no se puede publicar.
 *   2. REEMPLAZA al producto de la foto de referencia, no se suma. Si no, salen
 *      dos productos o el ajeno queda en el fondo.
 */
const PROMPT_PRODUCTO: Record<RubroPrompt, string> = {
  moda:
    'El producto es una prenda, un calzado o un accesorio, y va PUESTO: en la parte del ' +
    'cuerpo que ocupa en la imagen de referencia la prenda que se reemplaza, o donde se ' +
    'usa naturalmente si en esa imagen no hay ninguna. Tiene que salir exactamente como ' +
    'en sus fotos: mismo color, mismo estampado, mismos botones, costuras, bolsillos, ' +
    'logos y textos, mismo largo y mismas proporciones, sin inventar detalles que sus ' +
    'fotos no muestren ni modificar el diseño. De sus fotos sale ÚNICAMENTE la prenda: ' +
    'ignorá su fondo, la percha, el maniquí, las etiquetas y a la persona que la tenga ' +
    'puesta. Responde a la gravedad y a la pose con peso y caída reales, en el talle de ' +
    'quien la lleva —nada de capa superpuesta—. Si en la imagen de referencia hay otra ' +
    'prenda del mismo tipo, reemplazala por ésta: no la dupliques ni la dejes a la vista.',
  generico:
    'El producto va en el lugar que ocupa el producto en la imagen de referencia, o donde ' +
    'iría naturalmente si en esa imagen no hay ninguno, con el tamaño y la perspectiva ' +
    'que le corresponden. Tiene que salir exactamente como en sus fotos: misma forma, ' +
    'mismo material, mismo color, misma etiqueta, mismos textos y mismas proporciones, ' +
    'sin inventar texto ni modificar el diseño ni el packaging. De sus fotos sale ' +
    'ÚNICAMENTE el producto: ignorá su fondo, el soporte y las manos que lo sostengan. ' +
    'Tiene peso y presencia real en la escena: apoyado con su sombra de contacto, o ' +
    'sostenido con presión real de los dedos. Si en la imagen de referencia aparece otro ' +
    'producto, reemplazalo por éste: no lo dupliques ni lo dejes en el fondo.',
};

/**
 * Las reglas cuando NO hay producto. CONDICIONAL: no se sabe si la foto tiene un
 * producto —puede ser un paisaje, una modelo sola, una vidriera—, así que la regla es
 * "si hay uno, conservalo" y no "el producto es el de la foto". Reemplaza a
 * `PROMPT_PRODUCTO` en ese camino, con la misma regla dura (no inventar packaging).
 */
const PROMPT_SIN_PRODUCTO =
  'Si en la imagen de referencia aparece un producto, conservalo exactamente igual ' +
  '—misma forma, mismo envase, mismo color, misma etiqueta y mismas proporciones, sin ' +
  'inventar texto ni modificar el packaging— salvo que las indicaciones de abajo pidan ' +
  'cambiarlo. Lo que cambia es la escena, según esas indicaciones.';

/**
 * La MODELO, opcional. Dos formas, según qué se eligió en el picker:
 *   • una foto (modelo guardada o subida): viaja como referencia `modelo` y acá se la
 *     nombra por su etiqueta — la identidad no se describe en palabras;
 *   • una descripción ("Creá tu modelo"): son palabras y van directo al prompt.
 *
 * LA REGLA QUE NO PUEDE FALTAR es la de abajo: DESCARTAR a la persona de la foto a
 * replicar. Sin ella el modo devolvía la escena replicada con la persona de la
 * PLANTILLA y la modelo elegida no aparecía nunca
 * (ADR-2026-09-14-la-modelo-no-reemplazaba-a-la-persona-de-la-plantilla). Se entiende
 * por qué: todo lo demás del prompt —el nivel de fidelidad, "el resultado tiene que
 * reconocerse como la misma escena"— empuja a copiar esa imagen ENTERA, y el
 * `reference_protocol` que el backend agrega blinda la identidad contra la foto del
 * PRODUCTO pero no dice nada de la persona de la imagen de referencia. Decir "reemplazala
 * por esta modelo en la misma pose" no alcanzaba: "la misma pose" se lee como conservarla.
 */
export type ModeloParaPrompt =
  | { tipo: 'foto' }
  | { tipo: 'descripcion'; texto: string };

/**
 * El blindaje: de la persona de la foto a replicar se conserva la POSE y nada más.
 * Enumera los rasgos uno por uno —rostro, pelo, piel, cuerpo— en vez de decir "su
 * identidad": es la misma forma en que el `identidad_blindada` del backend descarta a la
 * persona que aparece en la foto del producto, que sí venía funcionando.
 */
const REGLA_MODELO_EN_ESCENA =
  'La persona que aparece en la imagen de referencia NO es la que tiene que salir: ' +
  'descartá por completo su rostro, su pelo, su piel y su cuerpo, y no los uses como ' +
  'guía de ningún tipo. De ella se conserva ÚNICAMENTE la pose, el encuadre, la actitud ' +
  'y el lugar que ocupa en la escena. Si en la imagen de referencia no hay ninguna ' +
  'persona, sumá a ésta usando o mostrando el producto de forma natural.';

/**
 * CÓMO SE ADAPTA la modelo a la escena y al producto — lo que faltaba: el prompt decía
 * QUIÉN sale y a quién descartar, pero no cómo esa persona entra en la foto ni cómo usa
 * el producto (ADR-2026-09-14-replicar-una-foto-habla-del-rubro). Común a las dos
 * familias: re-iluminada con la luz de la foto y con sus sombras de contacto. Lo que
 * cambia es la relación con el producto: en moda lo LLEVA PUESTO en su talle y con el
 * outfit completo; en los demás rubros lo usa, lo sostiene o lo muestra con contacto real.
 */
const ADAPTACION_MODELO: Record<RubroPrompt, string> = {
  moda:
    'Esa persona se integra a la escena como si la hubieran fotografiado ahí: ' +
    're-iluminada con la luz de la foto —misma dirección, dureza y color—, con las ' +
    'sombras de contacto y los reflejos que le tocan, y mirando a cámara o hacia donde ' +
    'miraba la persona original. Si hay producto, lo lleva PUESTO como corresponde a su ' +
    'cuerpo: el talle es el de ella, la prenda cae con su peso sobre su contextura y no ' +
    'se estira ni se encoge para copiar la foto del producto. Completá el outfit con lo ' +
    'que combine y la escena pida, sin tapar el producto; si los pies entran en cuadro, ' +
    'calzado puesto acorde al look.',
  generico:
    'Esa persona se integra a la escena como si la hubieran fotografiado ahí: ' +
    're-iluminada con la luz de la foto —misma dirección, dureza y color—, con las ' +
    'sombras de contacto y los reflejos que le tocan, y mirando a cámara o hacia donde ' +
    'miraba la persona original. Si hay producto, interactúa con él como lo hacía la ' +
    'persona original —lo sostiene, lo usa, lo muestra o posa a su lado— con contacto ' +
    'real: presión de los dedos, sombra del producto sobre la piel o la ropa, y una ' +
    'escala coherente con su mano y su cuerpo. Viste algo coherente con la escena y con ' +
    'el rubro del producto, sin taparlo.',
};

export function promptModelo(
  modelo: ModeloParaPrompt | null | undefined,
  rubro: RubroPrompt = 'generico',
): string | null {
  if (!modelo) return null;
  const quien =
    modelo.tipo === 'foto'
      ? `La persona que sale en el resultado es la de la imagen etiquetada ` +
        `"${ETIQUETAS.modelo}": misma cara, misma estructura del rostro, mismo cuerpo, ` +
        'mismo pelo y mismo tono de piel, reconocible como ella. '
      : modelo.texto.trim()
        ? `La persona que sale en el resultado tiene que ser ${modelo.texto.trim()}. `
        : null;
  if (!quien) return null;
  return quien + REGLA_MODELO_EN_ESCENA + ' ' + ADAPTACION_MODELO[rubro];
}

/** Que el resultado sea una foto y no un recorte pegado. También va siempre.
 *  "Campaña" a secas: el modo ya no es sólo de Belleza
 *  (ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza). */
const PROMPT_CIERRE =
  'El resultado es una fotografía, no un collage ni un recorte pegado: el producto está ' +
  'integrado en la escena, con la misma luz, las mismas sombras y los mismos reflejos ' +
  'que tendría si lo hubieran fotografiado ahí. Calidad de foto de campaña; ' +
  'si hay piel en cuadro, realista y sin retoque plástico.';

/**
 * Las tres opciones de FIDELIDAD, de la que menos copia a la que más (el panel las
 * muestra al revés: primero la default, "Igual a la foto").
 *
 * Qué decide: cuánto se copia de la foto subida. Y sólo eso — el producto se
 * respeta igual en los tres (`PROMPT_PRODUCTO`), porque "que se parezca menos a
 * la referencia" nunca puede significar "que se parezca menos al producto".
 *
 * Por qué tres y no un continuo: son tres INTENCIONES distintas —inspirarse,
 * adaptar, calcar—, no tres grados de una misma. Por eso tampoco es un slider: el
 * panel deja ver qué hace cada una —con su ejemplo— antes de elegirla
 * (ADR-2026-10-07-fidelidad-se-elige-como-producto-y-modelo).
 */
export type FidelidadId = 'libre' | 'equilibrada' | 'exacta';

export interface Fidelidad {
  id: FidelidadId;
  /** El valor de la card "Fidelidad" y el título de su opción en el panel. */
  nombre: string;
  /** La bajada de la opción en el panel. */
  descripcion: string;
  /**
   * La imagen de EJEMPLO de la opción: la miniatura de la card cuando está elegida y la
   * foto grande de su opción en el panel. Mientras falte, el panel deja el recuadro
   * vacío con su lugar reservado (ADR-2026-10-07-fidelidad-se-elige-como-producto-y-modelo).
   */
  ejemplo?: string;
  /** El trozo de prompt que aporta este nivel — ver `promptReplicar`. */
  prompt: string;
  /** El mismo nivel cuando NO hay producto: habla de "la imagen" (hay una sola) y de
   *  los cambios pedidos, no del producto que entra. */
  promptSinProducto: string;
}

export const FIDELIDADES: Fidelidad[] = [
  {
    id: 'libre',
    nombre: 'Inspirada en la foto',
    descripcion:
      'Atlas describe la foto y genera una nueva desde esa descripción, con tus cambios. Se parece en clima y estilo, no en los detalles.',
    prompt:
      'Tomá la imagen de referencia como INSPIRACIÓN y no como plantilla: quedate con su clima, ' +
      'su paleta de color y su estilo de fotografía, y armá con eso la mejor escena ' +
      'posible para este producto. Podés cambiar el encuadre, el fondo y la composición ' +
      'si con eso el producto se luce más.',
    promptSinProducto:
      'Tomá la imagen de referencia como INSPIRACIÓN y no como plantilla: quedate con su clima, su ' +
      'paleta de color y su estilo de fotografía, y armá con eso la mejor versión posible ' +
      'de la escena. Podés cambiar el encuadre, el fondo y la composición si con eso el ' +
      'producto se luce más.',
  },
  {
    id: 'equilibrada',
    nombre: 'Equilibrada',
    descripcion:
      'Genera una imagen nueva con la foto de referencia: misma escena, luz y colores, adaptando lo que cambies para que quede coherente.',
    prompt:
      'Copiá de la imagen de referencia la iluminación, la paleta de color, el fondo y el clima, ' +
      'y ADAPTÁ la composición al producto: si su forma o su proporción no entran en el ' +
      'encuadre original, reencuadrá lo necesario para que se vea entero y bien ' +
      'presentado. Adaptá también los colores, los textos y cualquier detalle que haga ' +
      'falta para que el cambio quede coherente con la escena. El resultado tiene que ' +
      'reconocerse como la misma escena.',
    promptSinProducto:
      'Copiá de la imagen de referencia la iluminación, la paleta de color, el fondo y el clima, y ' +
      'ADAPTÁ la composición a los cambios pedidos: reencuadrá lo necesario para que el ' +
      'producto se vea entero y bien presentado, y adaptá los colores, los textos y ' +
      'cualquier detalle que haga falta para que el cambio quede coherente. El resultado ' +
      'tiene que reconocerse como la misma escena.',
  },
  {
    id: 'exacta',
    nombre: 'Igual a la foto',
    descripcion:
      `La misma foto: misma escena, luz y colores. Cambia el producto, el ${palabraDeModeloActual().singular} o lo que pidas, y se adapta lo justo para que quede coherente.`,
    prompt:
      'Replicá la imagen de referencia al pie de la letra: el mismo encuadre, el mismo punto de ' +
      'vista, la misma composición, la misma iluminación y el mismo fondo. Lo ÚNICO que ' +
      'cambia es el producto.',
    promptSinProducto:
      'Replicá la imagen de referencia al pie de la letra: el mismo encuadre, el mismo punto de vista, ' +
      'la misma composición, la misma iluminación y el mismo fondo. Cambiá ÚNICAMENTE lo ' +
      'que pidan las indicaciones de abajo; todo lo demás queda igual.',
  },
];

/**
 * Con cuál arranca el modo: "Igual a la foto" (ADR-2026-10-07-fidelidad-es-un-desplegable,
 * supersede el default del medio de ADR-2026-09-13-replicar-una-foto-tres-pipelines).
 *
 * Quien sube una foto para recrearla casi siempre quiere ESA foto con su producto, y
 * es lo que espera ver al primer intento. Sin persona nueva genera con la instrucción de
 * Equilibrada, que adapta el encuadre cuando el producto no tiene la forma del de la
 * foto (ver `comoSeGenera`).
 */
export const FIDELIDAD_DEFAULT: FidelidadId = 'exacta';

/** Por dónde se genera — ver el encabezado del archivo. */
export type PipelineReplicar = 'describir' | 'referencia' | 'editar';

/**
 * CÓMO SE GENERA la opción elegida (ADR-2026-10-08-dos-opciones-de-fidelidad): el pipeline
 * y el nivel cuya instrucción viaja en el prompt.
 *   • Inspirada en la foto → `describir`, siempre (Nano Banana; ADR-2026-10-08-inspirada-va-con-nano-banana).
 *   • Igual a la foto SIN persona nueva → `referencia` con la instrucción de Equilibrada:
 *     Sunburst primero, que respeta el producto y adapta el encuadre si hace falta.
 *   • Igual a la foto CON persona nueva (foto o descripción, haya o no productos) →
 *     `editar`: el backend edita la foto con la instrucción de "Igual a la foto" y fusiona
 *     (Seedream primero, con la corrección del tinte verde).
 * `equilibrada` como opción ya no se ofrece; si llega, se trata como "Igual a la foto".
 */
export function comoSeGenera(
  id: FidelidadId,
  conPersona: boolean,
): { pipeline: PipelineReplicar; nivelDelPrompt: FidelidadId } {
  if (id === 'libre') return { pipeline: 'describir', nivelDelPrompt: 'libre' };
  return conPersona
    ? { pipeline: 'editar', nivelDelPrompt: 'exacta' }
    : { pipeline: 'referencia', nivelDelPrompt: 'equilibrada' };
}

/**
 * NIVEL BAJO, paso 1 — el prompt de VISIÓN: le pide a Gemini que escriba el prompt que
 * recrea la foto, dejando marcadores donde va lo que se reemplaza. El producto y la
 * persona NO se describen cuando van a reemplazarse: una descripción del envase
 * original haría que el generador intente dibujar ése y no el de la marca.
 */
export function promptParaDescribir(opts: {
  conProducto: boolean;
  modelo: ModeloParaPrompt | null;
  indicaciones: string;
  rubro?: RubroPrompt;
  cantidadProductos?: number;
}): string {
  const rubro = opts.rubro ?? 'generico';
  const n = Math.max(1, opts.cantidadProductos ?? 1);
  const partes = [
    'Mirá la imagen adjunta y escribí el prompt para un generador de imágenes que ' +
      'produzca una foto lo más parecida posible: la escena y el fondo, la iluminación, ' +
      'la paleta de color, el encuadre y el ángulo de cámara, el estilo fotográfico, qué ' +
      'hay en cuadro y dónde está cada cosa. En español, un solo párrafo.',
  ];
  if (opts.conProducto && n > 1) {
    partes.push(
      `Van a entrar ${n} productos de la marca. No describas los productos que aparecen: ` +
        'escribí «[PRODUCTO 1]», «[PRODUCTO 2]»… en el lugar y con el tamaño de cada uno, ' +
        `hasta [PRODUCTO ${n}]; si en la foto hay menos productos que ${n}, ubicá los que ` +
        'faltan donde se combinen naturalmente con la escena.',
    );
  } else if (opts.conProducto) {
    partes.push(
      'No describas el producto que aparece: escribí «[PRODUCTO]» en el lugar y con el ' +
        'tamaño que ocupa, porque se va a reemplazar por el producto real de la marca.',
    );
  } else {
    partes.push(
      'Si aparece un producto, describilo con precisión (forma, envase, color, etiqueta): ' +
        'tiene que salir el mismo.',
    );
  }
  if (opts.modelo) {
    partes.push(
      'No describas la cara ni el cuerpo de la persona: escribí «[MODELO]» donde está y ' +
        'describí sólo su pose, su actitud y ' +
        (rubro === 'moda'
          ? 'el resto de su ropa y su calzado'
          : 'qué hace con el producto (lo sostiene, lo usa, lo apoya, posa a su lado)') +
        ', porque se va a reemplazar por otra persona. Si no hay ninguna persona, sumá ' +
        '«[MODELO]» ' +
        (rubro === 'moda' ? 'llevando puesto el producto' : 'usando o mostrando el producto') +
        ' de forma natural.',
    );
  }
  const extra = opts.indicaciones.trim();
  if (extra) {
    partes.push(
      `Al describir, aplicá estos cambios pedidos por el usuario (el prompt tiene que ` +
        `describir la foto YA cambiada, no la original): ${extra}`,
    );
  }
  partes.push('Devolvé SOLO el prompt, sin título, sin comillas y sin explicaciones.');
  return partes.join(' ');
}

/**
 * NIVEL BAJO, paso 2 — el prompt que viaja a `creativo_plus`: la descripción que escribió
 * la visión, más quién ocupa cada marcador. Sin la foto como referencia: la referencia
 * es el texto, y lo único que viaja en imágenes es el producto y la modelo.
 */
export function promptNivelLibre(opts: {
  descripcion: string;
  conProducto: boolean;
  modelo: ModeloParaPrompt | null;
  rubro?: RubroPrompt;
  cantidadProductos?: number;
}): string {
  const rubro = opts.rubro ?? 'generico';
  const n = Math.max(1, opts.cantidadProductos ?? 1);
  const partes = [`Generá esta imagen: ${opts.descripcion.trim()}`];
  if (opts.conProducto) {
    partes.push(
      (n > 1
        ? `Donde dice [PRODUCTO 1], [PRODUCTO 2]… va cada uno de los ${n} productos REALES ` +
          `de la marca: el n viene en las imágenes etiquetadas "${ETIQUETAS.producto} n" ` +
          '(son productos DISTINTOS, no mezcles sus rasgos; van todos, cada uno una vez). '
        : `Donde dice [PRODUCTO] va el producto REAL de la marca, que viene en las imágenes ` +
          `etiquetadas "${ETIQUETAS.producto}" (son vistas del mismo producto). `) +
        (rubro === 'moda'
          ? 'Es una prenda, un calzado o un accesorio y va PUESTO, en el talle de quien lo ' +
            'lleva y con caída y peso reales. Tiene que salir exactamente como en sus ' +
            'fotos: mismo color, mismo estampado, mismos botones, costuras, logos y ' +
            'textos, mismo largo y mismas proporciones, sin inventar detalles ni ' +
            'modificar el diseño; de sus fotos ignorá el fondo, la percha, el maniquí y a ' +
            'la persona que lo tenga puesto.'
          : 'Tiene que salir exactamente como en sus fotos: misma forma, mismo material, ' +
            'mismo color, misma etiqueta, mismos textos y mismas proporciones, sin ' +
            'inventar texto ni modificar el diseño ni el packaging; de sus fotos ignorá el ' +
            'fondo, el soporte y las manos. Con peso y presencia real: apoyado con su ' +
            'sombra de contacto o sostenido con presión real.') +
        (n > 1 ? '' : ' Una sola unidad salvo que la descripción diga otra cosa.'),
    );
  }
  const quien =
    opts.modelo?.tipo === 'foto'
      ? `Donde dice [MODELO] va la persona de la imagen etiquetada "${ETIQUETAS.modelo}": ` +
        'misma cara, mismo cuerpo, mismo pelo y mismo tono de piel, reconocible como ella.'
      : opts.modelo?.tipo === 'descripcion' && opts.modelo.texto.trim()
        ? `Donde dice [MODELO] va ${opts.modelo.texto.trim()}.`
        : null;
  if (quien) partes.push(quien + ' ' + ADAPTACION_MODELO[rubro]);
  partes.push(PROMPT_CIERRE);
  return partes.join(' ');
}

export const fidelidadPorId = (id: FidelidadId): Fidelidad =>
  FIDELIDADES.find((f) => f.id === id) ??
  FIDELIDADES.find((f) => f.id === FIDELIDAD_DEFAULT)!;

/** Las DOS opciones en pantalla, la default primero (ADR-2026-10-08-dos-opciones-de-fidelidad).
 *  "Equilibrada" sigue en `FIDELIDADES` como nivel de prompt, no como opción. */
export const FIDELIDADES_EN_PANTALLA: Fidelidad[] = (['exacta', 'libre'] as const).map(
  (id) => fidelidadPorId(id),
);

/**
 * El prompt que viaja al backend.
 *
 * El orden importa: primero QUIÉN es cada referencia, después CUÁNTO copiar de
 * la escena (lo que decide la fidelidad), después las reglas del producto y el
 * cierre, y al final lo que escribió el usuario —sin envolver en ninguna
 * fórmula: lo último pesa más y tiene que poder corregir cualquier cosa de lo
 * anterior ("que el fondo sea rosa", "sin la modelo")—. Mismo criterio que
 * `promptVideoBelleza`.
 *
 * `conProducto` es la bifurcación: con producto son dos referencias y las reglas de
 * `PROMPT_PRODUCTO`; sin producto es una sola, el nivel habla de "la imagen" y las
 * reglas son las de `PROMPT_SIN_PRODUCTO` (el producto es el de la foto). Es un
 * parámetro obligatorio a propósito: que la pantalla lo diga en cada llamada, porque
 * mandar el prompt de dos referencias con una sola hace que el modelo se invente la
 * segunda.
 */
export function promptReplicar(opts: {
  fidelidad: FidelidadId;
  indicaciones: string;
  conProducto: boolean;
  /** La modelo elegida, si hay — ver `promptModelo`. */
  modelo?: ModeloParaPrompt | null;
  /** Moda (la prenda va puesta) o cualquier otro rubro (el producto se usa/apoya). */
  rubro?: RubroPrompt;
  /** Cuántos productos viajan (default 1). Con más de uno el prompt los numera. */
  cantidadProductos?: number;
}): string {
  const rubro = opts.rubro ?? 'generico';
  const n = Math.max(1, opts.cantidadProductos ?? 1);
  const nivel = fidelidadPorId(opts.fidelidad);
  const partes = opts.conProducto
    ? [
        promptIntro(n),
        nivel.prompt,
        PROMPT_PRODUCTO[rubro] + (n > 1 ? ' ' + PROMPT_VARIOS_PRODUCTOS : ''),
      ]
    : [PROMPT_INTRO_SIN_PRODUCTO, nivel.promptSinProducto, PROMPT_SIN_PRODUCTO];
  // La modelo va DESPUÉS de las reglas del producto y ANTES del cierre: es una regla más
  // de la escena, y el cierre ("es una foto, no un collage") tiene que abarcarla.
  const modelo = promptModelo(opts.modelo, rubro);
  if (modelo) partes.push(modelo);
  partes.push(PROMPT_CIERRE);
  const extra = (opts.indicaciones || '').trim();
  if (extra) partes.push(extra);
  return partes.join(' ');
}

/** Cuántas imágenes por tanda arranca el modo. El techo lo pone
 *  `FREEFORM_MAX_CANTIDAD` (6) en `agentGenerate` y el piso lo fija
 *  `ContadorImagenes` (1); el default es 1 porque cada intento es una lectura de
 *  la plantilla y conviene mirar una antes de pagar cuatro. */
export const CANTIDAD_DEFAULT = 1;

/**
 * El formato con el que arranca el modo si la plantilla no dice otra cosa: 9:16 —
 * ADR-2026-09-10-las-plantillas-de-belleza-arrancan-en-nueve-dieciseis.
 *
 * Era `'post'` (4:5) "el mismo default que el resto de la interfaz", pero el modo nació
 * en Belleza y Cuidado Personal, donde lo que se produce es vertical. Hoy es de todos
 * los rubros y el default casi no se ve (abajo se explica por qué), así que se deja.
 *
 * OJO — acá el default se ve poco: al elegir una plantilla, `elegirPlantilla` pisa el
 * formato con `formatoMasParecido()` de la proporción de esa foto, que es la promesa
 * del modo (replicar una referencia apaisada en 4:5 le recorta la composición). Así
 * que esto manda al abrir el modo y como fallback de `formatoMasParecido` cuando la
 * imagen no se puede medir; en cuanto hay plantilla elegida, manda la proporción.
 */
export const FORMATO_DEFAULT = 'story';

/**
 * El slug de formato cuya proporción se parece más a la de la plantilla.
 *
 * Por qué existe: el modo promete replicar una foto, y generar una referencia
 * apaisada en 4:5 recorta justo la composición que se quería copiar. Cuando el
 * usuario elige una plantilla, la pantalla mide la imagen y preselecciona esto —
 * es una SUGERENCIA: el picker de formato sigue estando y gana lo que el usuario
 * elija después.
 *
 * Los ratios son los mismos que mapea `formatSlugToAspect` en `agentGenerate`.
 * `billboard` (21:9) queda afuera a propósito: es un formato de cartel y ninguna
 * foto de referencia normal cae ahí sin que sea un accidente de medición.
 */
const FORMATOS_POR_RATIO: Array<{ slug: string; ratio: number }> = [
  { slug: 'story', ratio: 9 / 16 },
  { slug: 'portrait', ratio: 2 / 3 },
  { slug: 'post', ratio: 4 / 5 },
  { slug: 'square', ratio: 1 },
  { slug: 'editorial', ratio: 3 / 2 },
  { slug: 'banner', ratio: 16 / 9 },
];

export function formatoMasParecido(ancho: number, alto: number): string {
  if (!ancho || !alto) return FORMATO_DEFAULT;
  const ratio = ancho / alto;
  let mejor = FORMATOS_POR_RATIO[0];
  let mejorDistancia = Infinity;
  for (const f of FORMATOS_POR_RATIO) {
    // Distancia en escala logarítmica: comparar ratios crudos castiga de más a
    // los apaisados (entre 1 y 16/9 hay 0.78 de diferencia, y entre 9/16 y 1
    // sólo 0.44, aunque los dos sean "una vuelta de 90 grados").
    const d = Math.abs(Math.log(ratio / f.ratio));
    if (d < mejorDistancia) {
      mejorDistancia = d;
      mejor = f;
    }
  }
  return mejor.slug;
}
